// HeadlineTicker.js
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const headlines = [
  {
    text: "AI-powered screening for digital reputations",
    className: "text-[2rem] font-medium",
  },
  {
    text: "Scan smarter. Protect better",
    className: "text-[2.25rem] font-bold",
  },
  {
    text: "Catch risks before they go viral",
    className: "text-[2.25rem] font-bold",
  },
];

/**
 * HeadlineTicker - Animated headline carousel component
 *
 * Displays a rotating series of headlines with smooth fade transitions using Framer Motion.
 * Used primarily for marketing messages and taglines on authentication pages.
 *
 * The component cycles through predefined headlines with different font weights and sizes,
 * creating an engaging visual element that highlights key product benefits.
 *
 * @param {Object} props - Component props
 * @param {number} props.interval - Time in milliseconds between transitions (default: 3000)
 * @param {string} props.className - Additional CSS classes for styling
 *
 * @returns {JSX.Element} Animated headline ticker with smooth transitions
 */

export default function HeadlineTicker({
  interval = 3000, // 4 seconds per headline
  className = "",
}) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(
      () => setIndex((prev) => (prev + 1) % headlines.length),
      interval
    );
    return () => clearTimeout(timer);
  }, [index, interval]);

  return (
    <div
      className={`w-full overflow-hidden flex items-center py-4 px-10`}
      style={{ fontFamily: "Roboto, sans-serif" }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={headlines[index].text}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.5 }}
          className={`italic text-foreground leading-tight ${headlines[index].className} ${className}`}
        >
          {headlines[index].text}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
